import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Snackbar,
    Alert,
    CircularProgress
} from '@mui/material';
import axios from '../../../api/axios';

const EditDriverDialog = ({ open, onClose, userId, currentDriverId, onDriverUpdated }) => {
    const [driverId, setDriverId] = useState('');
    const [loading, setLoading] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('success');

    useEffect(() => {
        if (open && currentDriverId !== undefined) {
            setDriverId(currentDriverId.toString());
        }
    }, [open, currentDriverId]);

    const handleSave = async () => {
        if (!driverId.trim()) {
            setAlertMessage('Driver ID is required');
            setAlertSeverity('error');
            setAlertOpen(true);
            return;
        }

        const driverIdInt = parseInt(driverId, 10);
        if (isNaN(driverIdInt)) {
            setAlertMessage('Driver ID must be a valid number');
            setAlertSeverity('error');
            setAlertOpen(true);
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post('user/update/driver/', {
                user_id: userId,
                driver_id: driverIdInt
            });

            if (response.status === 200) {
                setAlertMessage('Driver updated successfully');
                setAlertSeverity('success');
                setAlertOpen(true);

                // Call the callback to update the parent component
                if (onDriverUpdated) {
                    onDriverUpdated(driverIdInt);
                }

                // Close dialog after a short delay
                setTimeout(() => {
                    onClose();
                }, 1000);
            }
        } catch (error) {
            console.error('Error updating driver:', error);
            setAlertMessage(error.response?.data?.error || 'Failed to update driver');
            setAlertSeverity('error');
            setAlertOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setDriverId('');
            onClose();
        }
    };

    const handleAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertOpen(false);
    };

    return (
        <>
            <Snackbar
                open={alertOpen}
                autoHideDuration={6000}
                onClose={handleAlertClose}
            >
                <Alert
                    onClose={handleAlertClose}
                    severity={alertSeverity}
                    sx={{ width: '100%' }}
                >
                    {alertMessage}
                </Alert>
            </Snackbar>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Driver</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Driver ID"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={driverId}
                        onChange={(e) => setDriverId(e.target.value)}
                        disabled={loading}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading}
                        startIcon={loading && <CircularProgress size={20} />}
                    >
                        {loading ? 'Updating...' : 'Update Driver'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EditDriverDialog;
